import type { CoachObservation, CoachSummary, CoachSummaryConfidence } from "./coachSummary";

export const COACH_PRIORITY_VERSION = "coach-priority-v1";

export type PriorityCategory = "most_important" | "watch_next" | "no_action_needed";

export type PriorityScoreComponents = {
  evidenceStrength: number;
  magnitude: number;
  athleteRelevance: number;
  actionability: number;
  uncertainty: number;
  sourceQuality: number;
  contradictionPenalty: number;
  missingContextPenalty: number;
};

export type CoachPriorityItem = {
  observationId: string;
  priorityCategory: PriorityCategory;
  headline: string;
  explanation: string;
  rationale: string;
  action: string;
  confidence: CoachSummaryConfidence;
  sourceObservationRank: number;
  score: number;
  scoreComponents: PriorityScoreComponents;
  ruleId: string;
};

export type CoachPrioritySummary = {
  mostImportantFinding: CoachPriorityItem | null;
  watchNext: CoachPriorityItem[];
  noActionNeeded: CoachPriorityItem[];
  limitations: string[];
  overallConfidence: CoachSummaryConfidence;
  generatedAt: string;
  priorityVersion: string;
};

type BuildCoachPriorityInput = {
  coachSummary: CoachSummary;
  generatedAt: string;
};

type ScoredObservation = {
  observation: CoachObservation;
  score: number;
  confidence: CoachSummaryConfidence;
  action: string;
  explanation: string;
  rationale: string;
  components: PriorityScoreComponents;
  watchCandidate: boolean;
};

function clamp(value: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, value));
}

function toConfidence(score: number): CoachSummaryConfidence {
  if (score >= 80) return "high";
  if (score >= 55) return "medium";
  return "low";
}

function confidenceScore(confidence: CoachSummaryConfidence): number {
  if (confidence === "high") return 92;
  if (confidence === "medium") return 72;
  return 46;
}

function evidenceTypeScore(evidenceType: CoachObservation["evidenceType"]): number {
  if (evidenceType === "observed") return 86;
  if (evidenceType === "calculated") return 82;
  if (evidenceType === "inferred") return 66;
  return 40;
}

function normalizedMetricName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
}

function findEvidenceNumber(observation: CoachObservation, aliases: string[]): number | null {
  const normalizedAliases = aliases.map(normalizedMetricName);
  for (const item of observation.evidence) {
    const metricName = normalizedMetricName(item.metric);
    if (!normalizedAliases.some(alias => metricName.includes(alias))) continue;
    if (typeof item.value === "number" && Number.isFinite(item.value)) {
      return item.value;
    }
    if (typeof item.value === "string") {
      const parsed = Number(item.value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function findEvidenceText(observation: CoachObservation, aliases: string[]): string | null {
  const normalizedAliases = aliases.map(normalizedMetricName);
  for (const item of observation.evidence) {
    const metricName = normalizedMetricName(item.metric);
    if (!normalizedAliases.some(alias => metricName.includes(alias))) continue;
    if (typeof item.value === "string") return item.value;
  }
  return null;
}

function athleteRelevance(domain: CoachObservation["domain"]): number {
  switch (domain) {
    case "pacing-consistency":
      return 91;
    case "aerobic-decoupling":
      return 88;
    case "durability":
      return 85;
    case "respiratory-execution":
      return 80;
    case "training-stimulus":
      return 77;
    case "ride-classification":
      return 69;
    case "source-quality":
      return 56;
    default:
      return 62;
  }
}

function sourceQualityFromEvidence(observation: CoachObservation): number {
  const coverage = findEvidenceNumber(observation, ["ride coverage", "valid-duration coverage", "activity completeness", "source agreement"]);
  const alignmentText = findEvidenceText(observation, ["alignment confidence", "respiratory alignment"]);

  let quality = coverage === null ? 64 : clamp(45 + coverage * 0.55);
  if (alignmentText) {
    const normalized = alignmentText.toLowerCase();
    if (normalized.includes("high")) quality += 16;
    if (normalized.includes("medium")) quality += 8;
    if (normalized.includes("low")) quality -= 12;
  }
  return clamp(quality);
}

function magnitudeFromDomain(observation: CoachObservation): number {
  if (observation.domain === "pacing-consistency") {
    const vi = findEvidenceNumber(observation, ["variability index"]);
    const powerChange = Math.abs(findEvidenceNumber(observation, ["power change"]) ?? 0);
    if (vi !== null) {
      if (vi > 1.06) return clamp(80 + Math.min(12, powerChange));
      if (vi <= 1.03) return clamp(74 + (powerChange <= 3 ? 6 : 0));
      return 62;
    }
    return 56;
  }

  if (observation.domain === "aerobic-decoupling") {
    const decoupling = Math.abs(findEvidenceNumber(observation, ["aerobic decoupling"]) ?? 0);
    if (decoupling > 5) return 86;
    if (decoupling > 3) return 70;
    return 74;
  }

  if (observation.domain === "durability") {
    const powerChange = Math.abs(findEvidenceNumber(observation, ["power change"]) ?? 0);
    if (powerChange >= 5) return 82;
    if (powerChange > 3) return 66;
    return 72;
  }

  if (observation.domain === "respiratory-execution") {
    const correlation = Math.abs(findEvidenceNumber(observation, ["ventilation-power correlation"]) ?? 0);
    if (correlation < 0.45) return 64;
    if (correlation >= 0.7) return 68;
    return 56;
  }

  if (observation.domain === "training-stimulus") {
    return 60;
  }

  if (observation.domain === "ride-classification") {
    const confidence = findEvidenceNumber(observation, ["classification confidence"]);
    return confidence !== null && confidence >= 88 ? 62 : 52;
  }

  if (observation.domain === "source-quality") {
    const agreement = findEvidenceNumber(observation, ["source agreement"]);
    return agreement !== null && agreement >= 80 ? 58 : 48;
  }

  return 54;
}

function contradictionPenalty(observation: CoachObservation): number {
  if (observation.domain === "pacing-consistency") {
    const vi = findEvidenceNumber(observation, ["variability index"]);
    const powerChange = Math.abs(findEvidenceNumber(observation, ["power change"]) ?? 0);
    if (vi !== null && vi <= 1.03 && powerChange >= 6) return 26;
  }

  if (observation.domain === "respiratory-execution") {
    const alignmentText = (findEvidenceText(observation, ["alignment confidence"]) ?? "").toLowerCase();
    if (alignmentText.includes("low") && observation.confidence === "high") return 22;
  }

  return 0;
}

function missingContextPenalty(observation: CoachObservation, limitations: string[]): number {
  const limitationsText = limitations.join(" ").toLowerCase();
  let penalty = 0;

  if (limitationsText.includes("historical trend comparison was unavailable")) {
    if (observation.domain === "respiratory-execution" || observation.domain === "aerobic-decoupling" || observation.domain === "durability") {
      penalty += 10;
    }
  }

  if (limitationsText.includes("no prescribed workout target")) {
    if (observation.domain === "training-stimulus") {
      penalty += 10;
    }
  }

  return clamp(penalty, 0, 24);
}

function uncertaintyScore(observation: CoachObservation, limitations: string[]): number {
  let uncertainty = observation.confidence === "high" ? 22 : observation.confidence === "medium" ? 42 : 66;

  if (observation.domain === "respiratory-execution") {
    const correlation = Math.abs(findEvidenceNumber(observation, ["ventilation-power correlation"]) ?? 0);
    if (correlation > 0 && correlation < 0.45) uncertainty += 26;
  }

  if (limitations.join(" ").toLowerCase().includes("historical trend comparison was unavailable")) {
    if (observation.domain === "respiratory-execution" || observation.domain === "training-stimulus") {
      uncertainty += 12;
    }
  }

  if (observation.explanation.toLowerCase().includes("limited")) {
    uncertainty += 8;
  }

  return clamp(uncertainty);
}

function chooseAction(observation: CoachObservation, components: PriorityScoreComponents, hasHistoricalLimit: boolean): string {
  if (observation.domain === "respiratory-execution") {
    const correlation = Math.abs(findEvidenceNumber(observation, ["ventilation-power correlation"]) ?? 0);
    if (correlation > 0 && correlation < 0.45) {
      return "Monitor across future comparable rides";
    }
  }

  if (components.contradictionPenalty >= 20 || components.uncertainty >= 72) {
    return "Review if repeated";
  }

  if (hasHistoricalLimit && (observation.domain === "respiratory-execution" || observation.domain === "training-stimulus")) {
    return "Compare against athlete baseline when available";
  }

  if (observation.domain === "source-quality" && components.sourceQuality < 62) {
    return "Add missing source for fuller interpretation";
  }

  if (components.magnitude >= 70 && components.evidenceStrength >= 72 && components.uncertainty <= 50) {
    return "Continue current execution approach";
  }

  return "No change needed";
}

function buildRationale(observation: CoachObservation, components: PriorityScoreComponents): string {
  const evidenceText = `evidence ${Math.round(components.evidenceStrength)}`;
  const magnitudeText = `magnitude ${Math.round(components.magnitude)}`;
  const relevanceText = `relevance ${Math.round(components.athleteRelevance)}`;
  const uncertaintyText = `uncertainty ${Math.round(components.uncertainty)}`;
  return `Prioritized from ${observation.domain.replace("-", " ")} using ${evidenceText}, ${magnitudeText}, ${relevanceText}, and ${uncertaintyText}.`;
}

function scoreObservation(observation: CoachObservation, limitations: string[]): ScoredObservation {
  const evidenceStrength = clamp((confidenceScore(observation.confidence) * 0.62) + (evidenceTypeScore(observation.evidenceType) * 0.38));
  const magnitude = magnitudeFromDomain(observation);
  const sourceQuality = sourceQualityFromEvidence(observation);
  const athleteRelevanceScore = athleteRelevance(observation.domain);
  const contradiction = contradictionPenalty(observation);
  const missingContext = missingContextPenalty(observation, limitations);
  const uncertainty = uncertaintyScore(observation, limitations);

  const components: PriorityScoreComponents = {
    evidenceStrength,
    magnitude,
    athleteRelevance: athleteRelevanceScore,
    actionability: 0,
    uncertainty,
    sourceQuality,
    contradictionPenalty: contradiction,
    missingContextPenalty: missingContext
  };

  const hasHistoricalLimit = limitations.some(item => item.toLowerCase().includes("historical trend comparison was unavailable"));
  const action = chooseAction(observation, components, hasHistoricalLimit);
  const actionability = action === "No change needed"
    ? 46
    : action === "Continue current execution approach"
      ? 56
      : action === "Monitor across future comparable rides"
        ? 64
        : action === "Compare against athlete baseline when available"
          ? 58
          : action === "Add missing source for fuller interpretation"
            ? 52
            : 68;

  components.actionability = actionability;

  const score = clamp(
    (components.evidenceStrength * 0.25)
    + (components.magnitude * 0.24)
    + (components.athleteRelevance * 0.20)
    + (components.actionability * 0.15)
    + (components.sourceQuality * 0.08)
    - (components.uncertainty * 0.12)
    - (components.contradictionPenalty * 0.10)
    - (components.missingContextPenalty * 0.06)
  );

  const watchCandidate = components.uncertainty >= 60
    || action === "Monitor across future comparable rides"
    || action === "Compare against athlete baseline when available"
    || action === "Review if repeated";

  const explanation = observation.explanation;
  const rationale = buildRationale(observation, components);
  const confidence = toConfidence(
    clamp((components.evidenceStrength * 0.55) + (components.sourceQuality * 0.2) + ((100 - components.uncertainty) * 0.25) - components.contradictionPenalty)
  );

  return {
    observation,
    score,
    confidence,
    action,
    explanation,
    rationale,
    components,
    watchCandidate
  };
}

function toPriorityItem(scored: ScoredObservation, category: PriorityCategory): CoachPriorityItem {
  return {
    observationId: scored.observation.id,
    priorityCategory: category,
    headline: scored.observation.headline,
    explanation: scored.explanation,
    rationale: scored.rationale,
    action: scored.action,
    confidence: scored.confidence,
    sourceObservationRank: scored.observation.rank,
    score: Math.round(scored.score),
    scoreComponents: scored.components,
    ruleId: `priority.${scored.observation.domain}.v1`
  };
}

function categoryMostImportant(scored: ScoredObservation[]): ScoredObservation | null {
  const candidates = scored
    .filter(item => item.components.evidenceStrength >= 60 && item.components.uncertainty <= 65 && item.confidence !== "low")
    .sort((left, right) => right.score - left.score);
  return candidates[0] ?? null;
}

function overallConfidence(
  mostImportant: CoachPriorityItem | null,
  watchNext: CoachPriorityItem[],
  noActionNeeded: CoachPriorityItem[]
): CoachSummaryConfidence {
  const all = [mostImportant, ...watchNext, ...noActionNeeded].filter((item): item is CoachPriorityItem => item !== null);
  if (!all.length) return "low";
  const average = all.reduce((sum, item) => sum + item.score, 0) / all.length;
  const confidenceAverage = all.reduce((sum, item) => sum + confidenceScore(item.confidence), 0) / all.length;
  return toConfidence(clamp((average * 0.55) + (confidenceAverage * 0.45)));
}

export function buildCoachPrioritySummary(input: BuildCoachPriorityInput): CoachPrioritySummary {
  const limitations = [...input.coachSummary.limitations];
  const scored = input.coachSummary.observations
    .map(observation => scoreObservation(observation, limitations))
    .sort((left, right) => right.score - left.score);

  const mostImportantScored = categoryMostImportant(scored);
  const usedObservationIds = new Set<string>();

  const mostImportant = mostImportantScored
    ? toPriorityItem(mostImportantScored, "most_important")
    : null;
  if (mostImportant) usedObservationIds.add(mostImportant.observationId);

  const watchNext = scored
    .filter(item => !usedObservationIds.has(item.observation.id))
    .filter(item => item.watchCandidate)
    .sort((left, right) => {
      const leftWatchScore = (left.components.uncertainty * 0.65) + (left.components.magnitude * 0.35);
      const rightWatchScore = (right.components.uncertainty * 0.65) + (right.components.magnitude * 0.35);
      return rightWatchScore - leftWatchScore;
    })
    .slice(0, 2)
    .map(item => {
      usedObservationIds.add(item.observation.id);
      return toPriorityItem(item, "watch_next");
    });

  const noActionNeeded = scored
    .filter(item => !usedObservationIds.has(item.observation.id))
    .filter(item => item.action === "No change needed" || item.action === "Continue current execution approach")
    .slice(0, 3)
    .map(item => {
      usedObservationIds.add(item.observation.id);
      return toPriorityItem(item, "no_action_needed");
    });

  return {
    mostImportantFinding: mostImportant,
    watchNext,
    noActionNeeded,
    limitations,
    overallConfidence: overallConfidence(mostImportant, watchNext, noActionNeeded),
    generatedAt: input.generatedAt,
    priorityVersion: COACH_PRIORITY_VERSION
  };
}
